import React, { useEffect, useState } from "react";
import { Box, Drawer, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { getClientWorkspaceApi } from "src/DAL/coachWorkspace/coachWorkspace";
import CoachNotebook from "./CoachNotebook";
import { CW, TextLink } from "./cwUi";

/**
 * CAL-NOTEBOOK (3 Nov, client video 17:03–17:13: "or I can go into their
 * calendar, and this notebook also appears"). A "Coach notebook" button on
 * the member's calendar pages (old Member profile + programme calendar)
 * that opens the SAME notebook as the review / feedback pages in a side
 * drawer: "This review" = the client's newest check-in, plus history and
 * General notes. A client who is not on any of your journeys gets the
 * General notes (same member notepad as before).
 */

export default function CalendarNotebook({ memberId }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [latestId, setLatestId] = useState(null); // null = not looked up yet, "" = none

  useEffect(() => {
    setLatestId(null);
  }, [memberId]);

  useEffect(() => {
    if (!open || !memberId || latestId !== null) return;
    let cancelled = false;
    getClientWorkspaceApi(memberId).then((result) => {
      if (cancelled) return;
      const newest = (result?.code === 200 ? result.journeys || [] : [])
        .map((j) => ({ id: j.latestSubmissionId, at: new Date(j.submissions?.[0]?.submittedAt || 0).getTime() }))
        .sort((a, b) => b.at - a.at)[0];
      setLatestId(newest?.id || "");
    });
    return () => {
      cancelled = true;
    };
  }, [open, memberId, latestId]);

  if (!memberId) return null;

  return (
    <>
      <Box
        component="button"
        type="button"
        data-testid="calendar-notebook-button"
        onClick={() => setOpen(true)}
        sx={{
          font: "inherit",
          position: "fixed",
          right: { xs: 16, md: 28 },
          bottom: { xs: 84, lg: 28 },
          zIndex: 1200,
          border: 0,
          borderRadius: "999px",
          px: 2.25,
          py: 1.25,
          bgcolor: CW.primary,
          color: "#fff",
          fontWeight: 700,
          fontSize: 14,
          boxShadow: "0 6px 18px rgba(0,0,0,0.18)",
          cursor: "pointer",
        }}
      >
        Coach notebook
      </Box>
      <Drawer anchor="right" open={open} onClose={() => setOpen(false)} PaperProps={{ sx: { width: { xs: "100%", sm: 440 }, bgcolor: CW.soft } }}>
        <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 13, color: CW.muted }}>Same notebook as the review and feedback pages</Typography>
          <IconButton size="small" aria-label="Close notebook" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ px: 2, pb: 3 }}>
          {open && latestId !== null ? (
            <CoachNotebook key={latestId || "general"} memberId={memberId} submissionId={latestId || undefined} />
          ) : (
            <Typography sx={{ fontSize: 13.5, color: CW.muted, p: 2 }}>Loading notes…</Typography>
          )}
          <TextLink onClick={() => navigate(`/coach/clients/${encodeURIComponent(memberId)}`)} sx={{ mt: 2, display: "inline-block", fontSize: 13.5 }}>
            Open client workspace →
          </TextLink>
        </Box>
      </Drawer>
    </>
  );
}
